import { FormStepLabel } from "@/components/ui/AddObject/schemas/form-steps.enum";
import { createConfigLoader } from "@/config";
import { DealType } from "@/enums/dealType";
import { PropertyType } from "@/enums/propertyType";

/**
 * Interface for describing a single form step
 */
export interface FormStep {
  /** Unique step identifier */
  id: FormStepLabel;
  /** Display label for the step */
  label: FormStepLabel;
  /** Step description (optional) */
  description?: string;
  /** Relative position of the step (used for sorting) */
  order: number;
  /** Condition for displaying the step */
  isVisible?: (dealType: DealType, propertyType?: PropertyType) => boolean;
  /** Additional step properties */
  config?: Record<string, any>;
}

/**
 * Interface for form steps configuration by deal types
 */
export interface FormStepConfig {
  /** Array of form steps available for this deal type */
  steps: FormStep[];
  /** Function to determine the order of steps for a given deal type and property type */
  getStepOrder: (
    dealType: DealType,
    propertyType?: PropertyType,
  ) => FormStepLabel[];
  /** Get step by ID */
  getStepById: (stepId: FormStepLabel) => FormStep | undefined;
  /** Get step ID for a step number */
  getStepIdForStepNumber: (
    step: number,
    dealType: DealType,
    propertyType?: PropertyType,
  ) => FormStepLabel;
  /** Get total number of steps for a given deal type and property type */
  getTotalSteps: (dealType: DealType, propertyType?: PropertyType) => number;
}

/**
 * Interface for form steps configuration data
 */
export interface IFormStepsConfig {
  steps: FormStep[];
  stepOrder: Record<
    DealType,
    Partial<Record<PropertyType | "default", FormStepLabel[]>>
  >;
}

/**
 * All available form steps
 */
const ALL_FORM_STEPS: FormStep[] = [
  {
    id: FormStepLabel.BASIC,
    label: FormStepLabel.BASIC,
    order: 1,
    isVisible: () => true, // Basic step is always displayed
  },
  {
    id: FormStepLabel.LAYOUTS,
    label: FormStepLabel.LAYOUTS,
    order: 2,
    isVisible: (dealType) => dealType === DealType.PRIMARY,
  },
  {
    id: FormStepLabel.MEDIA,
    label: FormStepLabel.MEDIA,
    order: 3,
    isVisible: () => true,
  },
  {
    id: FormStepLabel.LOCATION,
    label: FormStepLabel.LOCATION,
    order: 4,
    isVisible: () => true,
  },
  {
    id: FormStepLabel.ADDITIONAL,
    label: FormStepLabel.ADDITIONAL,
    order: 5,
    isVisible: () => true,
  },
  {
    id: FormStepLabel.REALTOR,
    label: FormStepLabel.REALTOR,
    order: 6,
    isVisible: (dealType) => dealType !== DealType.PRIMARY,
  },
  {
    id: FormStepLabel.PREVIEW,
    label: FormStepLabel.PREVIEW,
    order: 7,
    isVisible: () => true,
  },
];

/**
 * Step order for each deal type
 */
const STEP_ORDER: Record<
  DealType,
  Partial<Record<PropertyType | "default", FormStepLabel[]>>
> = {
  [DealType.PRIMARY]: {
    default: [
      FormStepLabel.BASIC,
      FormStepLabel.LAYOUTS,
      FormStepLabel.MEDIA,
      FormStepLabel.LOCATION,
      FormStepLabel.ADDITIONAL,
      FormStepLabel.PREVIEW,
    ],
  },
  [DealType.BUY]: {
    default: [
      FormStepLabel.BASIC,
      FormStepLabel.MEDIA,
      FormStepLabel.LOCATION,
      FormStepLabel.ADDITIONAL,
      FormStepLabel.REALTOR,
      FormStepLabel.PREVIEW,
    ],
  },
  [DealType.RENT]: {
    default: [
      FormStepLabel.BASIC,
      FormStepLabel.MEDIA,
      FormStepLabel.LOCATION,
      FormStepLabel.ADDITIONAL,
      FormStepLabel.REALTOR,
      FormStepLabel.PREVIEW,
    ],
  },
};

// Default form steps configuration
const defaultFormStepsConfig: IFormStepsConfig = {
  steps: ALL_FORM_STEPS,
  stepOrder: STEP_ORDER,
};

// Load configuration with country-specific overrides
const formStepsConfigData = createConfigLoader(
  defaultFormStepsConfig,
  "formSteps",
);

/**
 * Function to get step order for a specific deal type and property type
 */
const getStepOrder = (
  dealType: DealType,
  propertyType?: PropertyType,
): FormStepLabel[] => {
  const dealConfig =
    formStepsConfigData.stepOrder[dealType] ||
    formStepsConfigData.stepOrder[DealType.PRIMARY];
  const specificConfig = propertyType && dealConfig[propertyType];

  return specificConfig || dealConfig.default || [];
};

/**
 * Function to get a step by its ID
 */
const getStepById = (stepId: FormStepLabel): FormStep | undefined => {
  return formStepsConfigData.steps.find((step) => step.id === stepId);
};

/**
 * Function to get step ID for a step number
 */
const getStepIdForStepNumber = (
  step: number,
  dealType: DealType = DealType.PRIMARY,
  propertyType?: PropertyType,
): FormStepLabel => {
  const stepOrder = getStepOrder(dealType, propertyType);

  // Normalize step number
  const normalizedStep = Math.max(1, Math.min(step, stepOrder.length));

  // Return step ID
  return stepOrder[normalizedStep - 1];
};

/**
 * Function to get total number of steps
 */
const getTotalSteps = (
  dealType: DealType,
  propertyType?: PropertyType,
): number => {
  return getStepOrder(dealType, propertyType).length;
};

/**
 * Exported form step configuration
 */
export const formStepConfig: FormStepConfig = {
  steps: formStepsConfigData.steps,
  getStepOrder,
  getStepById,
  getStepIdForStepNumber,
  getTotalSteps,
};
